import { ApiProblem } from '../problem';

/**
 * How one v1 DTO field maps to the legacy column the editor reads. Legacy rows are what the generic
 * route returned: the entity's properties in snake_case, with jsonb held as JSON text unless the
 * entity types the column as an object.
 */
export type FieldRule =
  /** The same value under the legacy column name. */
  | string
  /** A jsonb column the generic route returned as JSON text: v1 carries the parsed value. */
  | { legacy: string; as: 'jsonText' }
  /** A jsonb column the entity holds as an object: the value is passed as is. */
  | { legacy: string; as: 'json' }
  /** An enum whose v1 value (key) differs from the stored value. */
  | { legacy: string; as: 'enum'; values: Readonly<Record<string, string>> };

/** One rule for every field of the DTO, no more and no fewer. */
export type FieldSpec<D> = { readonly [K in keyof D]-?: FieldRule };

type LegacyKeyOf<R> = R extends string ? R : R extends { legacy: infer K extends string } ? K : never;
type OrNull<V, T> = null extends V ? T | null : T;
type LegacyValueOf<R, V> = R extends string
  ? V
  : R extends { as: 'jsonText' }
    ? OrNull<V, string>
    : R extends { as: 'json' }
      ? V
      : R extends { as: 'enum'; values: infer M }
        ? OrNull<V, M[keyof M]>
        : never;

/** The legacy row a DTO maps to: the columns its spec names, typed from the DTO. */
export type LegacyRow<S, D> = {
  -readonly [K in keyof S & keyof D as LegacyKeyOf<S[K]>]: LegacyValueOf<S[K], D[K]>;
};

/** A body of v1 fields, each optional: what a create or PATCH sends. */
export type BodyOf<D, K extends keyof D> = { [P in K]?: D[P] };

export interface FieldMap<D, S extends FieldSpec<D>> {
  /** The legacy row for a DTO: every column the DTO carries. */
  toLegacy(dto: D): LegacyRow<S, D>;
  /** The DTO for a legacy row toLegacy produced; a column the row lacks reads as null. */
  toDto(row: LegacyRow<S, D>): D;
  /**
   * The v1 body for legacy columns. Undefined values are skipped (as JSON.stringify did), and so are
   * the `serverSet` columns, which the server fills itself (the caller as owner, timestamps). Any
   * other column outside `writable` throws an ApiProblem `field_not_writable` rather than being
   * dropped silently.
   */
  toBody<K extends keyof D & keyof S>(
    row: Partial<LegacyRow<S, D>>,
    writable: readonly K[],
    serverSet?: readonly (keyof LegacyRow<S, D>)[],
  ): BodyOf<D, K>;
}

const legacyKey = (rule: FieldRule): string => (typeof rule === 'string' ? rule : rule.legacy);

function parseJsonText(resource: string, column: string, value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    throw new ApiProblem({ status: 0, code: 'invalid_value', field: column, title: `${resource}: '${column}' is not valid JSON.` });
  }
}

function toLegacyValue(rule: FieldRule, value: unknown): unknown {
  if (value === undefined || value === null) return null;
  if (typeof rule === 'string') return value;
  switch (rule.as) {
    case 'jsonText':
      return JSON.stringify(value);
    case 'json':
      return value;
    case 'enum':
      return rule.values[value as string] ?? value;
  }
}

function toV1Value(resource: string, rule: FieldRule, column: string, value: unknown): unknown {
  if (typeof rule === 'string' || value === null) return value;
  switch (rule.as) {
    case 'jsonText':
    case 'json':
      return parseJsonText(resource, column, value);
    case 'enum': {
      const match = Object.entries(rule.values).find(([, stored]) => stored === value);
      if (!match) {
        throw new ApiProblem({ status: 0, code: 'invalid_value', field: column, title: `${resource}: '${column}' cannot be '${String(value)}'.` });
      }
      return match[0];
    }
  }
}

/** The two-way mapping for one resource, from its spec. `resource` names it in errors. */
export function fieldMap<D, S extends FieldSpec<D>>(resource: string, spec: S): FieldMap<D, S> {
  const rules = Object.entries(spec) as [string, FieldRule][];
  const ruleOf = (field: string) => (spec as Record<string, FieldRule>)[field];

  function toLegacy(dto: D): LegacyRow<S, D> {
    const row: Record<string, unknown> = {};
    for (const [field, rule] of rules) row[legacyKey(rule)] = toLegacyValue(rule, (dto as Record<string, unknown>)[field]);
    return row as LegacyRow<S, D>;
  }

  function toDto(row: LegacyRow<S, D>): D {
    const dto: Record<string, unknown> = {};
    for (const [field, rule] of rules) {
      const column = legacyKey(rule);
      const value = (row as Record<string, unknown>)[column];
      dto[field] = value === undefined ? null : toV1Value(resource, rule, column, value);
    }
    return dto as D;
  }

  function toBody<K extends keyof D & keyof S>(
    row: Partial<LegacyRow<S, D>>,
    writable: readonly K[],
    serverSet: readonly (keyof LegacyRow<S, D>)[] = [],
  ): BodyOf<D, K> {
    const fields = new Map((writable as readonly string[]).map(field => [legacyKey(ruleOf(field)), field]));
    const skipped = new Set<string>(serverSet as readonly string[]);
    const body: Record<string, unknown> = {};
    for (const [column, value] of Object.entries(row as Record<string, unknown>)) {
      if (value === undefined || skipped.has(column)) continue;
      const field = fields.get(column);
      if (!field) {
        throw new ApiProblem({
          status: 0,
          code: 'field_not_writable',
          field: column,
          title: `${resource}: '${column}' cannot be written through /api/v1 here.`,
        });
      }
      body[field] = toV1Value(resource, ruleOf(field), column, value);
    }
    return body as BodyOf<D, K>;
  }

  return { toLegacy, toDto, toBody };
}

/** The legacy id column's value, which every v1 create needs (the client generates ids). */
export function requireId(resource: string, column: string, value: unknown): string {
  if (typeof value === 'string' && value) return value;
  throw new ApiProblem({ status: 0, code: 'id_required', field: column, title: `${resource}: creating one needs '${column}', generated by the client.` });
}
